import { useLogger } from './LoggerProvider';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';

type NetworkContextType = {
  isOnline: boolean;
};

const NetworkContext = React.createContext<NetworkContextType>(undefined);

const NetworkProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);
  const { logger } = useLogger();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };
    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Cancel all active queries and API requests when going offline
  React.useEffect(() => {
    if (!isOnline) {
      logger.warn('Network offline', {
        action: 'NetworkProvider.useEffect.!isOnline',
        data: {
          isOnline,
        },
      });
    }
  }, [isOnline, queryClient, logger]);

  return (
    <NetworkContext.Provider
      value={{
        isOnline,
      }}
    >
      {children}
    </NetworkContext.Provider>
  );
};

export default NetworkProvider;

export const useNetwork = () => {
  const context = React.useContext(NetworkContext);
  if (!context) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
};
